import isNil from "~/kernel/validation/isNil";
import type { GetCompany } from "./GetCompany";
import to24HourFormat from "~/kernel/dateTime/to24HourFormat";
import ROUTES from "~/kernel/routing/ROUTES";
import MAX_SCORE from "~/constants/review/MAX_SCORE";
import type SerializeType from "~/kernel/server/SerializeType";
import type { CompanyPageReview } from "./CompanyPageReview";
import { buildReviewMetadata } from "./buildReviewMetadata";

export function buildSearchEngineMetadata(
  company: GetCompany,
  reviews: SerializeType<CompanyPageReview>[],
) {
  const address = {
    "@type": "PostalAddress",
    streetAddress: company.address,
    addressLocality: company.city,
    postalCode: company.zipCode,
  };

  function dayOpeningHors(
    dayOfWeek: string,
    startMinute: number | null,
    endMinute: number | null,
  ) {
    if (isNil(startMinute) || isNil(endMinute)) {
      return null;
    }

    return {
      "@type": "OpeningHoursSpecification",
      dayOfWeek,
      opens: to24HourFormat(startMinute),
      closes: to24HourFormat(endMinute),
    };
  }

  return [
    {
      "script:ld+json": {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        image: [company.bannerUrl],
        name: company.name,
        url: ROUTES.company.path({ params: { slug: company.slug } }),
        potentialAction: {
          "@type": "ReserveAction",
          target: {
            "@type": "EntryPoint",
            urlTemplate: ROUTES.newBooking.path({
              params: { slug: company.slug },
            }),
            actionPlatform: [
              "http://schema.org/DesktopWebPlatform",
              "http://schema.org/MobileWebPlatform",
            ],
          },
          result: {
            "@type": "Reservation",
            name: "Boka hundtrim",
          },
        },
        address,
        email: {
          "@type": "Text",
          email: company.contactEmail,
        },
        ...aggregateRating(company),
        review: reviews.map(buildReviewMetadata),
        telephone: company.phone,
        openingHoursSpecification: [
          dayOpeningHors(
            "Monday",
            company.mondayOpensMinute,
            company.mondayClosesMinute,
          ),
          dayOpeningHors(
            "Tuesday",
            company.tuesdayOpensMinute,
            company.tuesdayClosesMinute,
          ),
          dayOpeningHors(
            "Wednesday",
            company.wednesdayOpensMinute,
            company.wednesdayClosesMinute,
          ),
          dayOpeningHors(
            "Thursday",
            company.thursdayOpensMinute,
            company.thursdayClosesMinute,
          ),
          dayOpeningHors(
            "Friday",
            company.fridayOpensMinute,
            company.fridayClosesMinute,
          ),
          dayOpeningHors(
            "Saturday",
            company.saturdayOpensMinute,
            company.saturdayClosesMinute,
          ),
          dayOpeningHors(
            "Sunday",
            company.sundayClosesMinute,
            company.sundayClosesMinute,
          ),
        ].filter(Boolean),
      },
    },
  ];
}

function aggregateRating(company: GetCompany) {
  if (company.reviewCount < 1 || isNil(company.score)) {
    return {};
  }

  return {
    aggregateRating: {
      "@type": "AggregateRating",
      reviewCount: company.reviewCount,
      bestRating: MAX_SCORE,
      ratingValue: company.score,
    },
  };
}
